.page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 36px 24px 80px;
}

.backBtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--muted);
  font-size: 14.5px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 0;
  margin-bottom: 24px;
  background: none;
  border: none;
  transition: color .15s;
  font-family: 'Roboto', sans-serif;
}
.backBtn:hover { color: var(--blue); }

/* ── Stepper ──────────────────────────────────────────────────────────── */

.steps {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
}

.step {
  display: flex;
  align-items: center;
  gap: 10px;
}

.circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
  border: 2px solid var(--border);
  color: var(--muted);
  background: white;
  font-family: 'Google Sans', sans-serif;
  transition: all .2s;
}

.stepActive .circle { border-color: var(--blue); color: var(--blue); }
.stepDone .circle   { background: var(--blue); border-color: var(--blue); color: white; border-width: 0; }

.stepLabel {
  font-size: 13.5px;
  color: var(--muted);
  font-weight: 500;
  white-space: nowrap;
}
.stepActive .stepLabel { color: var(--blue); }
.stepDone .stepLabel   { color: var(--ink2); font-weight: 600; }

.line {
  flex: 1;
  height: 1px;
  background: var(--border);
  margin: 0 12px;
  transition: background .2s;
}
.lineDone { background: var(--blue); }

/* ── Card ─────────────────────────────────────────────────────────────── */

.card {
  background: white;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  min-height: 520px;
}

.cardHeader {
  padding: 24px 28px 20px;
  border-bottom: 1px solid var(--border);
}

.cardTitle {
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -.01em;
  font-family: 'Google Sans', sans-serif;
}

.cardSub {
  font-size: 13.5px;
  color: var(--muted);
  margin-top: 3px;
  line-height: 1.55;
}

.cardBody { padding: 24px 28px; }

/* ── Summary bar ──────────────────────────────────────────────────────── */

.summaryBar {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 13px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.journeyBadge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--muted);
  background: white;
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 4px 10px;
}

/* ── Form fields ──────────────────────────────────────────────────────── */

.field { margin-bottom: 22px; }
.fieldLast { margin-bottom: 0; }

.label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 7px;
  letter-spacing: .01em;
}

.req { color: var(--red); margin-left: 2px; }

.hint {
  font-size: 13px;
  color: var(--muted2);
  margin-top: 5px;
  line-height: 1.55;
}

.actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.twoActions {
  margin-top: 26px;
  display: flex;
  justify-content: space-between;
}

/* ── Buttons ──────────────────────────────────────────────────────────── */

.btnPrimary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 22px;
  background: var(--blue);
  color: white;
  border: none;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Google Sans', sans-serif;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(26,115,232,.3);
  transition: background .15s, box-shadow .15s, transform .1s;
}
.btnPrimary:hover {
  background: var(--blue-dk);
  box-shadow: 0 2px 6px rgba(26,115,232,.4);
  transform: translateY(-1px);
}
.btnPrimary:active { transform: translateY(0); }
.btnPrimary:disabled { opacity: .5; cursor: default; transform: none; }

.btnOutline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 22px;
  background: white;
  color: var(--muted);
  border: 1.5px solid var(--border);
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Google Sans', sans-serif;
  cursor: pointer;
  transition: border-color .15s, color .15s, background .15s;
}
.btnOutline:hover {
  border-color: #bdc1c6;
  color: var(--ink);
  background: var(--bg);
}
.btnOutline:disabled { opacity: .5; cursor: default; }

.saveError {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  color: var(--red);
  margin-top: 16px;
}

.rejectedRow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--red);
  margin-bottom: 12px;
}

/* ── Channel picker ───────────────────────────────────────────────────── */

.searchWrap { position: relative; margin-bottom: 16px; }

.searchIcon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted2);
  pointer-events: none;
}

.searchInput {
  width: 100%;
  padding: 11px 14px 11px 38px;
  border: 1px solid var(--border);
  border-radius: 4px;
  font-size: 15px;
  font-family: 'Roboto', sans-serif;
  color: var(--ink);
  background: white;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.searchInput:focus {
  border-color: var(--blue);
  box-shadow: 0 0 0 2px rgba(26,115,232,.15);
}

.channelList { margin-bottom: 4px; }

.channelOption {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 15px 17px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 8px;
  transition: border-color .15s, background .15s;
}
.channelOption:hover  { border-color: #bdc1c6; background: #fafafa; }
.channelSelected { border-color: var(--blue); background: var(--blue-lt); }

.coIcon { color: var(--muted); flex-shrink: 0; }
.channelSelected .coIcon { color: var(--blue); }

.coName {
  flex: 1;
  font-weight: 600;
  font-size: 15px;
  color: var(--ink);
}

.coCheck {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all .15s;
}
.channelSelected .coCheck {
  background: var(--blue);
  border-color: var(--blue);
  color: white;
}

/* ── Journey type picker ──────────────────────────────────────────────── */

.journeyGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.journeyOption {
  border: 1.5px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 24px 22px;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s, background .15s;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.journeyOption:hover {
  border-color: var(--blue);
  box-shadow: var(--shadow);
  background: var(--blue-lt);
}

.journeyIcon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.journeyTitle {
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  font-family: 'Google Sans', sans-serif;
}

.journeyDesc {
  font-size: 13.5px;
  color: var(--muted);
  line-height: 1.55;
  flex: 1;
}

.journeyArrow {
  color: var(--blue);
  margin-top: 4px;
}

/* ── Feature grid ─────────────────────────────────────────────────────── */

.featureGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 4px;
}

.featureCard {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px;
  cursor: pointer;
  transition: border-color .15s, background .15s, box-shadow .15s;
  position: relative;
}
.featureCard:hover    { border-color: #bdc1c6; box-shadow: var(--shadow-sm); }
.featureSelected { border-color: var(--blue); background: var(--blue-lt); }

.fcCheck {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .15s;
}

.fcIcon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  background: var(--bg);
  color: var(--muted);
  transition: background .15s, color .15s;
}

.fcTitle {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
  font-family: 'Google Sans', sans-serif;
}

.fcDesc {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.55;
}
.featureSelected .fcDesc { color: var(--ink2); }

/* ── Live screen ──────────────────────────────────────────────────────── */

.liveWrap {
  display: flex;
  justify-content: center;
  padding-top: 20px;
}

.liveCard {
  background: white;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  padding: 52px 40px 44px;
  max-width: 560px;
  width: 100%;
  text-align: center;
  animation: slideUp .4s ease;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

.liveIconWrap {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
}

.liveIconRing {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid var(--green);
  opacity: 0;
  animation: ringPop .5s ease .2s forwards;
}

@keyframes ringPop {
  0%   { opacity: 0; transform: scale(.6); }
  60%  { opacity: 1; transform: scale(1.15); }
  100% { opacity: .3; transform: scale(1); }
}

.liveIcon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--green-lt);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--green);
  animation: iconPop .4s cubic-bezier(.34,1.56,.64,1) .1s both;
}

@keyframes iconPop {
  from { opacity: 0; transform: scale(.5); }
  to   { opacity: 1; transform: scale(1); }
}

.liveTitle {
  font-size: 26px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -.02em;
  font-family: 'Google Sans', sans-serif;
  margin-bottom: 8px;
}

.liveSub {
  font-size: 15px;
  color: var(--muted);
  margin-bottom: 28px;
  line-height: 1.6;
}

.liveFeatures {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 18px 20px;
  text-align: left;
  margin-bottom: 28px;
}

.liveFeaturesLabel {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--muted2);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin-bottom: 14px;
}

.liveFeaturesGrid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.liveFeatureItem {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  color: var(--ink2);
}

.liveFeatureIcon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.liveActions {
  margin-top: 8px;
}
